import { ensureLocalProductFromWebCache } from '../../../../catalog/infra/ensureLocalProductFromWebCache';
import { getCatalogRamSnapshotCache } from '../../../../catalog/infra/catalogRamSnapshotCache';
import { NonInventoriableProductError, StockProductNotFoundError, StockTransferValidationError } from '../../domain/stockErrors';
export type InventoriableProductShape = {
  itemType?: string | null;
  isMisc?: boolean | null;
};

export const isInventoriableProduct = (product: InventoriableProductShape | null | undefined) =>
  product?.isMisc !== true && product?.itemType !== 'service';

function throwIfNotInventoriable(row: InventoriableProductShape | null | undefined): void {
  if (!row) {
    throw new StockProductNotFoundError();
  }
  if (!isInventoriableProduct(row)) {
    throw new NonInventoriableProductError();
  }
}

export const assertProductIsInventoriable = async (productId: string): Promise<void> => {
  if (!productId) {
    throw new StockProductNotFoundError();
  }

  // Web ram-only: SQLite es un stub vacío. El listado ya vino del snapshot en memoria.
  const cached = getCatalogRamSnapshotCache()?.products.find((p) => p.id === productId);
        throwIfNotInventoriable(
          cached
            ? { itemType: cached.item_type, isMisc: cached.is_misc === true }
            : null
        );
        return;
};

/**
 * Valida que la variante exista, pertenezca al producto y esté activa.
 * Espejo del guard de backend (apply_stock_adjustment_event #692).
 * Si `variantId` es null/undefined, no hace nada (camino parent).
 */
export const assertVariantBelongsToProduct = async (
  variantId: string | null | undefined,
  productId: string,
): Promise<void> => {
  if (!variantId) return;

  const cached = getCatalogRamSnapshotCache()?.productVariants.find(
          (pv) => String(pv.id) === variantId,
        );
        if (!cached) {
          throw new StockTransferValidationError('La variante no existe.');
        }
        const pvProductId = String(
          (cached as { product_id?: string | null }).product_id ?? '',
        );
        const pvActive = (cached as { is_active?: boolean | null }).is_active;
        if (pvProductId !== productId) {
          throw new StockTransferValidationError('La variante no pertenece al producto.');
        }
        if (pvActive === false) {
          throw new StockTransferValidationError('La variante está inactiva.');
        }
        return;
};

/**
 * Si el producto tiene variantes, el stock se ajusta POR variante (parent = suma).
 * Ajustar el parent directo deja la card "en stock" pero variantes en 0 (audit #5).
 */
export const assertNoParentStockOnVariantProduct = async (
  productId: string,
  variantId: string | null | undefined,
): Promise<void> => {
  if (variantId) return;
  const hasVariants =
    getCatalogRamSnapshotCache()?.productVariants.some(
      (pv) =>
        String((pv as { product_id?: string | null }).product_id ?? '') === productId &&
        (pv as { is_active?: boolean | null }).is_active !== false,
    ) ?? false;
  if (hasVariants) {
    throw new StockTransferValidationError(
      'Este producto tiene variantes: el stock se ajusta por variante.',
    );
  }
};

/** Resuelve un label legible para una variante desde la cache RAM (suffix o attributes). */
export const resolveVariantLabel = (variantId: string | null | undefined): string | null => {
  if (!variantId) return null;
  const cached = getCatalogRamSnapshotCache()?.productVariants.find(
    (pv) => String(pv.id) === variantId,
  );
  if (!cached) return null;
  const suffix = String((cached as { name_suffix?: string | null }).name_suffix ?? '').trim();
  if (suffix) return suffix;
  const attrsRaw = (cached as { attributes_json?: string | null }).attributes_json;
  if (attrsRaw) {
    try {
      const attrs = JSON.parse(attrsRaw) as Record<string, string>;
      const label = Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(' · ');
      if (label) return label;
    } catch {
      /* ignore */
    }
  }
  return 'Variante';
};
